import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';
import { map, size, take } from 'es-toolkit/compat';
import React from 'react';
import { useClipboard } from 'use-clipboard-copy';

const SHOWN_ERRORS = 3;

interface ExportErrorAlertProps {
  title: string;
  errors: string[];
  // Text the user can copy into a support request; see buildExportErrorReport.
  report: string;
}

// An export that failed with an error, as opposed to one with nothing to export. The user is told to contact
// Nucleai support and gets the details to send; a bounded sample of the errors stays visible.
export const ExportErrorAlert: React.FC<ExportErrorAlertProps> = ({ title, errors, report }) => {
  const clipboard = useClipboard({ copiedTimeout: 2000 });
  const hidden = size(errors) - SHOWN_ERRORS;
  return (
    <Alert severity="error">
      <AlertTitle>{title}</AlertTitle>
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2">
          Querying the results failed. Please contact Nucleai support and include the error details.
        </Typography>
        <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }} aria-label="Errors">
          {map(take(errors, SHOWN_ERRORS), (error, index) => (
            <Typography key={`${error}-${index}`} component="li" variant="caption">
              {error}
            </Typography>
          ))}
          {hidden > 0 && (
            <Typography component="li" variant="caption">
              and {hidden} more, included in the details
            </Typography>
          )}
        </Stack>
        <Button size="small" color="inherit" variant="outlined" onClick={() => clipboard.copy(report)}>
          {clipboard.copied ? 'Copied' : 'Copy error details'}
        </Button>
      </Stack>
    </Alert>
  );
};
